/**
 * YesNoSwitchPure
 *
 * Pure component for the "Contact Connected?" branching UI in wait_for_connect steps.
 * Shows yes/no paths with configurable delay for the "no" path.
 */
import { useState } from 'react';

import { cn } from '../cn';
import { Popup } from '../Popup';
import { Tooltip } from '../Tooltip';
import { Button, Input, Label } from '../Input2';
import CadenceVerticalBar from '../CadenceVerticalBar';

export type CadenceStepStatePure =
  | 'not-started' | 'interrupted' | 'skipped' | 'finished'
  | 'canceled' | 'action-required' | 'responded' | 'contact_unavailable';

export type YesNoSwitchPureProps = {
  /** Delay in milliseconds before assuming not connected */
  delay: number;
  /** Current step state */
  state?: CadenceStepStatePure;
  /** Callback when delay is changed */
  onChange: (delay: number) => void;
  /** Whether to show rounded corners */
  rounded?: boolean;
  className?: string;
};

/** Convert days to milliseconds */
function daysToMs(days: number): number {
  return days * 24 * 60 * 60 * 1000;
}

/** Convert milliseconds to days */
function msToDays(ms: number): number {
  return Math.round(ms / (24 * 60 * 60 * 1000));
}

export function YesNoSwitchPure({ delay, rounded, state, onChange, className }: YesNoSwitchPureProps) {
  const [editing, setEditing] = useState(false);
  const day = msToDays(delay);

  return (
    <div className={cn('flex flex-col items-start self-stretch', className)}>
      <CadenceVerticalBar className="ml-2 h-1" />

      <div className="flex-start flex">
        <Tooltip label={state === 'skipped' ? 'You are connected' : undefined}>
          <div
            className={cn(
              'p-2 px-4 @xs/yesno:p-1 @xs/yesno:px-2 text-xs tracking-tight',
              { 'rounded-tl-md': rounded },
              'bg-lynch-500 border-l-2 border-t-2 border-b-2 border-lynch-800 font-bold text-white',
            )}>
            Contact Connected?
          </div>
        </Tooltip>
        {state !== 'skipped' && (
          <div
            className={cn(
              'cursor-pointer rounded-r-md p-2 px-4 @xs/yesno:p-1 @xs/yesno:px-2 text-xs',
              'bg-fair-pink-100 border-r-2 border-t-2 border-b-2 border-fair-pink-400 font-medium tracking-tight text-fair-pink-700 hover:bg-fair-pink-300 active:bg-fair-pink-400',
            )}
            onClick={() => setEditing(true)}>
            No, After <strong>{day}</strong> days:
          </div>
        )}
      </div>

      <div
        className={cn(
          'rounded-br-md p-2 px-4 @xs/yesno:p-1 @xs/yesno:px-2 text-xs',
          { 'rounded-bl-md': rounded },
          'bg-mantis-200 border-2 border-t-0 border-mantis-300 font-bold text-mantis-700',
        )}>
        Yes!
      </div>
      <CadenceVerticalBar className="ml-2 h-0 grow" />

      {editing && (
        <YesNoSwitchPopup
          delay={delay}
          onClose={() => setEditing(false)}
          onChangeDelay={(newDelay) => {
            onChange(newDelay);
            setEditing(false);
          }}
        />
      )}
    </div>
  );
}

type YesNoSwitchPopupProps = {
  delay: number;
  onClose: () => void;
  onChangeDelay: (delay: number) => void;
};

function YesNoSwitchPopup({ delay, onClose, onChangeDelay }: YesNoSwitchPopupProps) {
  const [days, setDays] = useState(msToDays(delay));

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onChangeDelay(daysToMs(days));
  };

  return (
    <Popup onClose={onClose}>
      <div className="mb-4">How many days should we wait before assuming you are not connected</div>
      <form onSubmit={handleSubmit} className="flex max-w-lg flex-col gap-2">
        <div className="flex flex-col gap-1">
          <Label>Days</Label>
          <Input
            type="number"
            value={days}
            onChange={(e) => setDays(Number(e.target.value))}
            placeholder="# of days"
            autoFocus
          />
        </div>
        <Button type="submit" color="blue">
          Update
        </Button>
      </form>
    </Popup>
  );
}

export default YesNoSwitchPure;
